import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { ICalendarConfig } from "@miplatform/types";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";

export const CalendarTabCard = ({
  config,
  isReadOnly,
  isFirstInList,
  isLastInList,
  onDeleteClick,
  onEditClick,
  onReorderUp,
  onReorderDown,
}: Readonly<CalendarTabCardProps>) => {
  const { classes, theme } = useStyles();

  return (
    <Box className={classes.configListCardContent}>
      <Box className={classes.configListCard}>
        <Box className={classes.configListCardContent}>
          <Box className={classes.configListCardText}>
            <Box className={classes.configListCardTextLine}>
              <Typography variant="HD2" color={theme.palette.primary.main}>
                {config.name || "New Calendar Tab"}
              </Typography>
              {config.attachedTray && (
                <>
                  <Divider orientation="vertical" />
                  <Typography
                    variant="BD2"
                    fontSize={theme.fontSize.medium}
                    color={theme.palette.grey.medium}
                  >
                    {config.attachedTray.name}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
        {!isReadOnly && (
          <Box className={classes.configListCardActions}>
            <MiIconButton onClick={() => onDeleteClick(config.id)}>
              <MiIcon
                path={mdiTrashCanOutline}
                color={theme.palette.red.main}
              />
            </MiIconButton>
            <MiIconButton onClick={() => onEditClick(config.id)}>
              <MiIcon
                path={mdiPencilOutline}
                color={theme.palette.primary.main}
              />
            </MiIconButton>
          </Box>
        )}
      </Box>
      {!isReadOnly && (
        <Box className={classes.arrowsContainer}>
          <MiIcon
            path={mdiChevronUp}
            color={theme.palette.primary.main}
            onClick={() => onReorderUp(config.id)}
            disabled={isFirstInList}
            size={0.85}
          />
          <MiIcon
            path={mdiChevronDown}
            color={theme.palette.primary.main}
            onClick={() => onReorderDown(config.id)}
            disabled={isLastInList}
            size={0.85}
          />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  configListCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    flex: 1,
  },
  configListCardContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  configListCardActions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  configListCardText: {
    display: "flex",
    flexDirection: "column",
  },
  configListCardTextLine: {
    height: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  arrowsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export interface CalendarTabCardProps {
  config: ICalendarConfig;
  attachedTray?: string;
  isReadOnly?: boolean;
  isFirstInList?: boolean;
  isLastInList?: boolean;
  onDeleteClick: (tabId: string) => void;
  onEditClick: (tabId: string) => void;
  onReorderUp: (tabId: string) => void;
  onReorderDown: (tabId: string) => void;
}
